"use client";

import { COMPANY } from "@/lib/facts";
import { useState } from "react";
import { MapPin, ExternalLink } from "lucide-react";

/**
 * Google-Maps-Einbettung für die DMD Studio GmbH.
 *
 * - Klick-zum-Laden (datenschutzfreundlich, Google-Daten werden erst nach
 *   Zustimmung geladen)
 * - Zeigt den Firmen-Eintrag „DMD Studio GmbH" direkt als Pin mit Info-
 *   Fenster in der echten Kartenansicht an (Koordinaten 50.3006108,
 *   10.1934825)
 */
const EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(`${COMPANY.legalName},${COMPANY.street},${COMPANY.zip} ${COMPANY.city}`)}&ll=${COMPANY.geo.latitude},${COMPANY.geo.longitude}&z=17&hl=de&output=embed`;

const DIRECT_URL = COMPANY.mapsUrl;

type GoogleMapProps = {
  height?: number;
  className?: string;
};

export function GoogleMap({ height = 380, className = "" }: GoogleMapProps) {
  const [loaded, setLoaded] = useState(false);

  if (loaded) {
    return (
      <div
        className={`overflow-hidden rounded-xl border ${className}`}
        style={{ borderColor: "var(--border)" }}
      >
        <iframe
          src={EMBED_URL}
          width="100%"
          height={height}
          style={{ border: 0, display: "block" }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="DMD Studio GmbH – Am Kebigsgraben 21, 97616 Salz"
        />
        <div
          className="flex items-center justify-between border-t px-4 py-2 text-xs"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--card-bg)",
            color: "var(--text-tertiary)",
          }}
        >
          <span>DMD Studio GmbH · Am Kebigsgraben 21, 97616 Salz</span>
          <a
            href={DIRECT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-brand-500 hover:underline"
          >
            In Google Maps öffnen
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setLoaded(true)}
      className={`group flex w-full flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-12 transition-all hover:border-brand-500/50 hover:bg-brand-500/5 ${className}`}
      style={{ borderColor: "var(--border)", minHeight: height }}
    >
      <div className="rounded-full bg-brand-500/10 p-4 transition-transform group-hover:scale-110">
        <MapPin className="h-8 w-8 text-brand-500" />
      </div>
      <div className="text-center">
        <p
          className="text-sm font-semibold"
          style={{ color: "var(--text)" }}
        >
          Google Maps laden
        </p>
        <p
          className="mt-1 max-w-sm text-xs"
          style={{ color: "var(--text-tertiary)" }}
        >
          Mit dem Laden der Karte akzeptieren Sie die Datenschutzerklärung von
          Google. Anschließend sehen Sie DMD Studio GmbH direkt in der
          Kartenansicht.
        </p>
      </div>
    </button>
  );
}
